import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Fetches panel data on mount and on reload(). Each panel owns its own
 * loading/error state so one failing panel never blanks the dashboard.
 * fetcher identity is captured in a ref, so callers may pass inline fns.
 */
export function usePanelData<T>(fetcher: () => Promise<T>) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [tick, setTick] = useState(0);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const reload = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    fetcherRef
      .current()
      .then((d) => {
        if (active) setData(d);
      })
      .catch(() => {
        if (active) setError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [tick]);

  return { data, loading, error, reload };
}
